import {AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {GeneralGatewayModel} from '../../../../../../models/data-models/general-gateway-model';
import {NgModel} from '@angular/forms';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {DataService} from "../../../../../../services/data.service";
import {
  GatewayType,
  IDeviceData,
  isReadOnlyModuleType,
  isSameModuleType,
  IUvsModule
} from "../../../../../../interfaces/juconnGateway.response";
import {UvsModuleModel} from "../../../../../../models/uvs-module.model";
import {
  EditModuleModalComponent,
  EditModuleModalData,
  EditModuleModalResult
} from "../../../../../../components/edit-module-modal/edit-module-modal.component";
import {MqttGetModuleConfigUvsModuleDto} from "../../../../../../interfaces/mqtt/mqtt-get-module-config-uvs-module.dto";
import {DeviceDataService} from '../../../../../../services/device-data.service';
import {AvailableSite, Customer} from '../../../../../../models/customer-model';
import {IInstallationDataResponse} from '../../../../../../interfaces/installation-data.response';
import {ShareService} from "../../../../../../services/share.service";
import {ValidationService} from "../../../../../../services/validation.service";
import {debounceTime, share} from "rxjs/operators";
import {
  ModuleConfigurationModalComponent
} from "../../../../../../components/module-configuration-modal/module-configuration-modal.component";
import {
  SendConfigChange,
  SendConfigChangeItem,
  SendConfigModalComponent,
  SendConfigModalData
} from "../../../../../../components/send-config-modal/send-config-modal.component";
import {Dialog} from "@angular/cdk/dialog";
import {MqttGetModuleConfigDto} from "../../../../../../interfaces/mqtt/mqtt-get-module-config.dto";

export interface ModuleSensorRow {
  label: string;
  value: string;
}

export interface ModuleStatus {
  kind: 'ok' | 'wait' | 'err';
  text: string;
}

@Component({
  selector: 'app-install-general-gateway',
  templateUrl: './install-general-gateway.component.html',
  styleUrls: ['./install-general-gateway.component.scss'],
})
export class InstallGeneralGatewayComponent implements OnInit, AfterViewInit, OnDestroy {
  public gatewayId: string;
  public model: GeneralGatewayModel;

  @ViewChildren(NgModel) inputs: QueryList<NgModel>;

  public currentModuleComparison: number = 0;
  public configurationBeingSent = false;

  public customerOptions: Customer[];
  public installedSiteOptions: AvailableSite[];
  public plannedSiteOptions: AvailableSite[];
  public installationData: IInstallationDataResponse;


  public moduleConfig: MqttGetModuleConfigDto;
  public isModuleConfigLoading: boolean;
  public moduleConfigLoadedAt: Date;

  public deviceData: IDeviceData;
  public isDeviceDataLoading = false;
  public expandedModules = new Set<number>();

  public configWaitTime = 50; // seconds

  private timerForConfigWaitTimeSubject: BehaviorSubject<number> = new BehaviorSubject(null);
  public timerForConfigWaitTime: Observable<number> = this.timerForConfigWaitTimeSubject.asObservable();

  protected readonly GatewayType = GatewayType;

  private dataSubscriptions: Subscription[] = [];
  private inputSubscriptions: Subscription[] = [];

  constructor(
    private dataService: DataService,
    private deviceDataService: DeviceDataService,
    public shareService: ShareService,
    public validationService: ValidationService,
    private dialog: Dialog
  ) {
    this.dataSubscriptions.push(this.shareService.gatewayId$.subscribe(gatewayId => {
      this.gatewayId = gatewayId;
    }));
    this.gatewayId = this.shareService.getGatewayId();

    this.dataSubscriptions.push(this.deviceDataService.installationData$.subscribe(data => {
      this.installationData = data;
    }));
    this.model = this.shareService.generalGateway || new GeneralGatewayModel();
    this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
    this.dataSubscriptions.push(this.shareService.generalGateway$.subscribe((data) => {
      this.model = data || new GeneralGatewayModel();
      this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
    }));

    this.dataSubscriptions.push(this.shareService.customerOptions$.subscribe(customers => {
      this.customerOptions = customers;
      if (!this.model.additionalGateway && this.model.selectedCustomer?.plannedSites?.length > 0) {
        this.model.selectedCustomer?.plannedSites.sort((a, b) => this.getSiteName(a).localeCompare(this.getSiteName(b)));
        this.plannedSiteOptions = this.model.selectedCustomer.plannedSites;
      }
      if (this.model.additionalGateway && this.model.selectedCustomer?.installedSites?.length > 0) {
        this.model.selectedCustomer?.installedSites.sort((a, b) => this.getSiteName(a).localeCompare(this.getSiteName(b)));
        this.installedSiteOptions = this.model.selectedCustomer.installedSites;
      }
    }));

    this.dataSubscriptions.push(this.deviceDataService.deviceData$.subscribe(data => {
      if (!data) return;
      this.deviceData = data.deviceData;
      this.isDeviceDataLoading = data.isLoading;
    }));

    this.dataSubscriptions.push(this.deviceDataService.moduleConfig$.subscribe((moduleConfig => {
      if (moduleConfig == null) return;
      this.moduleConfig = moduleConfig.moduleConfig;
      this.isModuleConfigLoading = moduleConfig.isLoading;
      if (!moduleConfig.isLoading && moduleConfig.moduleConfig) {
        this.moduleConfigLoadedAt = new Date();
      }
    })));
  }

  async ngOnInit(): Promise<void> {
    await this.loadCustomerAndSitesData();
  }

  ngAfterViewInit(): void {
    this.inputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.shareService.generalGateway = this.model;
      }));
    });

    this.inputs.changes.subscribe(() => {
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      this.inputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.shareService.generalGateway = this.model;
        }));
      });
    });
  }

  ngOnDestroy(): void {
    this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.dataSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  addModuleSelection() {
    const used = this.model.uvsModules.map(module => +module.address);
    let address = 1;
    while (used.includes(address)) {
      address++;
    }
    this.openEditModule({modulId: address + '', uvsType: GatewayType.SM01, address}, true);
  }

  public isReadOnlyModule(module: UvsModuleModel): boolean {
    return isReadOnlyModuleType(module?.uvsType);
  }

  editModule(index: number) {
    if (this.isReadOnlyModule(this.model.uvsModules[index])) {
      return;
    }
    this.openEditModule(this.model.uvsModules[index], false, index);
  }

  removeModuleSelection(index: number) {
    if (this.isReadOnlyModule(this.model.uvsModules[index])) {
      return;
    }
    const module = this.model.uvsModules[index];
    if (this.getGatewayModule(module.address)) {
      module.markedForRemoval = true;
    } else {
      this.model.uvsModules.splice(index, 1);
    }
    this.expandedModules.clear();
    this.shareService.generalGateway = this.model;
    this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
  }

  undoRemoveModule(index: number) {
    this.model.uvsModules[index].markedForRemoval = false;
    this.shareService.generalGateway = this.model;
    this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
  }

  public get activeModulesCount(): number {
    return (this.model?.uvsModules || []).filter(m => !m.markedForRemoval).length;
  }

  toggleModule(address: number) {
    if (this.expandedModules.has(address)) {
      this.expandedModules.delete(address);
    } else {
      this.expandedModules.add(address);
    }
  }

  public getGatewayModule(address: number): MqttGetModuleConfigUvsModuleDto {
    return this.moduleConfig?.uvsModule?.find(module =>
      this.normalizeAddress(module.typ, module.address) === +address);
  }

  public getModuleStatus(module: UvsModuleModel): ModuleStatus {
    if (this.isModuleConfigLoading || !this.moduleConfig) {
      return {kind: 'wait', text: 'Nicht geladen'};
    }
    const configured = this.getGatewayModule(module.address);
    if (!configured || !isSameModuleType(configured.typ, module.uvsType)) {
      return {kind: 'wait', text: 'Noch nicht gesendet'};
    }
    if (!this.deviceData?.uvsModule) {
      return {kind: 'wait', text: 'Gesendet, noch nicht geprüft'};
    }
    const detected = this.getDetectedModule(module.address);
    if (!detected) {
      return {kind: 'err', text: 'Nicht erkannt'};
    }
    if (!isSameModuleType(detected.moduleInfo.uvsType, module.uvsType)) {
      return {kind: 'err', text: 'Gateway meldet ' + this.getTypeLabel(detected.moduleInfo.uvsType)};
    }
    return {kind: 'ok', text: 'Vom Gateway erkannt'};
  }

  public getModuleError(module: UvsModuleModel): string {
    const status = this.getModuleStatus(module);
    if (status.kind !== 'err') {
      return null;
    }
    const detected = this.getDetectedModule(module.address);
    if (detected) {
      return `Adresse ${module.address}: Gateway meldet ${this.getTypeLabel(detected.moduleInfo.uvsType)}, konfiguriert ist ${this.getTypeLabel(module.uvsType)}. Typ oder Adresse korrigieren und erneut senden.`;
    }
    return `Adresse ${module.address} (${this.getTypeLabel(module.uvsType)}): Das Gateway findet hier kein Modul. Adresse am Modul und Verkabelung prüfen, Konfiguration korrigieren und erneut senden.`;
  }

  public get nextBlockedReason(): string {
    if (this.configurationBeingSent || this.isDeviceDataLoading) {
      return 'Daten werden geladen …';
    }
    if (this.unsentChangesCount > 0) {
      return 'Weiter ist möglich, sobald die Änderungen gesendet und die Daten neu geladen wurden.';
    }
    return null;
  }

  public get unsentChangesCount(): number {
    const modules = (this.model?.uvsModules || []).filter(m => !m.markedForRemoval);
    const gatewayModules = this.moduleConfig?.uvsModule || [];
    const changedOrNew = modules.filter(module => {
      const configured = this.getGatewayModule(module.address);
      return !configured || !isSameModuleType(configured.typ, module.uvsType);
    }).length;
    const removed = gatewayModules.filter(gw => !modules.some(module => +module.address === this.normalizeAddress(gw.typ, gw.address))).length;
    return changedOrNew + removed;
  }

  public discardModuleChanges() {
    const detectedModules = this.deviceData?.uvsModule || [];
    if (!detectedModules.length) {
      return;
    }
    const getAddress = (module: IUvsModule) => this.normalizeAddress(module.moduleInfo.uvsType, module.moduleInfo.address);

    const uvsModules = this.model.uvsModules.filter(module =>
      detectedModules.some(detected => getAddress(detected) === module.address));
    uvsModules.forEach(module => module.markedForRemoval = false);

    detectedModules.forEach(detected => {
      const address = getAddress(detected);
      let uvsModule = uvsModules.find(m => m.address === address);
      if (!uvsModule) {
        uvsModule = {
          modulId: detected.moduleInfo.modulID,
          uvsType: detected.moduleInfo.uvsType,
          address,
          sensorParameters: {},
          measuredValues: {}
        } as UvsModuleModel;
        uvsModules.push(uvsModule);
      }
      if (!isSameModuleType(uvsModule.uvsType, detected.moduleInfo.uvsType)) {
        uvsModule.sensorTypeName = undefined;
        uvsModule.sensorParameters = {b: undefined, referenceResistance: undefined};
        uvsModule.measuredValues = {
          sensorResistance: undefined,
          outsideTemperature: undefined,
          temperatureDisplayedByHeatingSystem: undefined,
          timestamp: undefined
        };
        uvsModule.sensorNameOptions = [];
      }
      uvsModule.modulId = detected.moduleInfo.modulID;
      uvsModule.uvsType = detected.moduleInfo.uvsType;
      uvsModule.address = address;
    });

    this.model.uvsModules = uvsModules.sort((a, b) => a.address - b.address);
    this.expandedModules.clear();
    this.shareService.generalGateway = this.model;
    this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
  }

  public get unrecognizedModulesCount(): number {
    return this.model?.uvsModules?.filter(module => this.getModuleStatus(module).kind === 'err').length || 0;
  }

  public getConfigRows(address: number): ModuleSensorRow[] {
    const config = this.getGatewayModule(address);
    if (!config) {
      return [];
    }
    const rows: ModuleSensorRow[] = [];
    const num = (v: number, unit = '') => v === null || v === undefined ? '–' : String(v).replace('.', ',') + (unit ? ' ' + unit : '');
    /*config.currentLoopDelta?.forEach((v, i) => rows.push({
      label: 'Grenzwert Pumpe ' + (i + 1),
      value: num(v, 'A')
    }));
    if (config.lightThreshold != undefined) rows.push({label: 'Schwelle Licht', value: num(config.lightThreshold)});
    if (config.lekageThreshold != undefined) rows.push({
      label: 'Schwelle Leckage',
      value: num(config.lekageThreshold)
    });
    if (config.temperatureThreshold !== undefined) rows.push({
      label: 'Temperatur-Delta',
      value: num(config.temperatureThreshold, '°C')
    });*/
    config.irUartCfg?.forEach(ir => rows.push({label: 'IR-Zähler Typ (Eingang ' + ir.input + ')', value: ir.device}));
    if (config.sensorTypeName != undefined) rows.push({label: 'Fühlertyp', value: config.sensorTypeName});
    if (config.sensorParameters) {
      rows.push({label: 'B-Wert', value: num(config.sensorParameters.b)});
      rows.push({label: 'Referenzwiderstand', value: num(config.sensorParameters.referenceResistance, 'Ω')});
    }
    return rows;
  }

  private getDetectedModule(address: number): IUvsModule {
    return this.deviceData?.uvsModule?.find(m =>
      this.normalizeAddress(m.moduleInfo.uvsType, m.moduleInfo.address) === +address);
  }

  private normalizeAddress(type: string, address: number): number {
    const isOtm = type === GatewayType.OTM01 || type === GatewayType.OTM02 || type === GatewayType.OTM03;
    return isOtm ? +address - 59 : +address;
  }

  public getTypeLabel(type: string): string {
    switch (type) {
      case GatewayType.SM01:
        return 'Sensormodul';
      case GatewayType.SM02:
        return 'Zählermodul SM02';
      case GatewayType.SM03:
        return 'Zählermodul SM03';
      case GatewayType.SM04:
        return 'Zählermodul SM04';
      case GatewayType.SM05:
        return 'Zählermodul SM05';
      case GatewayType.OTM01:
        return 'Außentemperaturmodul 1';
      case GatewayType.OTM02:
        return 'Außentemperaturmodul 2';
      case GatewayType.OTM03:
        return 'Außentemperaturmodul 3';
      default:
        return type || 'Unbekanntes Modul';
    }
  }

  private openEditModule(module: UvsModuleModel, isNew: boolean, index?: number) {
    const data: EditModuleModalData = {
      address: module.address,
      uvsType: module.uvsType,
      isNew,
      usedAddresses: this.model.uvsModules.filter((_, i) => i !== index).map(m => ({
        address: +m.address,
        uvsType: m.uvsType
      })),
      module: isNew ? undefined : module,
      gatewayConfig: isNew ? undefined : this.getGatewayModule(module.address),
    };
    this.dialog.open<EditModuleModalResult>(EditModuleModalComponent, {data}).closed.subscribe(result => {
      if (!result) return;
      if (isNew) {
        this.model.uvsModules.push({modulId: result.address + '', ...result});
      } else {
        this.model.uvsModules[index] = {...module, ...result};
      }
      this.model.uvsModules.sort((a, b) => a.address - b.address);
      this.expandedModules.clear();
      this.shareService.generalGateway = this.model;
      this.currentModuleComparison = this.compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData();
    });
  }

  canSendModuleSelections() {
    const modules = this.model?.uvsModules?.filter(module => !module?.markedForRemoval) || [];
    return modules.length >= 1 && modules.every(module => module && !!module.uvsType);
  }

  public reviewAndSend() {
    const data: SendConfigModalData = {changes: this.buildChanges(), errors: this.buildSendErrors()};
    this.dialog.open<boolean>(SendConfigModalComponent, {data}).closed.subscribe(confirmed => {
      if (confirmed) {
        this.sendModuleSelections();
      }
    });
  }

  private buildSendErrors(): string[] {
    const errors: string[] = [];
    const seen = new Set<string>();
    this.model.uvsModules.filter(m => !m.markedForRemoval && !isReadOnlyModuleType(m.uvsType)).forEach(m => {
      const isOtm = this.isOtmType(m.uvsType);
      const key = (isOtm ? 'otm' : 'sm') + m.address;
      if (seen.has(key)) {
        errors.push(`Adresse ${m.address} ist doppelt vergeben.`);
      }
      seen.add(key);
    });
    return errors;
  }

  private isOtmType(type: string): boolean {
    return type === GatewayType.OTM01 || type === GatewayType.OTM02 || type === GatewayType.OTM03;
  }

  private buildChanges(): SendConfigChange[] {
    const changes: SendConfigChange[] = [];
    const modules = this.model.uvsModules.filter(m => !m.markedForRemoval && !isReadOnlyModuleType(m.uvsType));
    modules.forEach(module => {
      const typeLabel = this.getTypeLabel(module.uvsType);
      const gw = this.moduleConfig ? this.getGatewayModule(module.address) : undefined;
      if (!gw) {
        changes.push({address: module.address, typeLabel, kind: 'new', items: this.describeConfig(module)});
        return;
      }
      const items: SendConfigChangeItem[] = [];
      if (!isSameModuleType(gw.typ, module.uvsType)) {
        items.push({label: 'Modultyp', from: this.getTypeLabel(gw.typ), to: typeLabel, reset: true});
      } else {
        items.push(...this.diffConfig(module, gw));
      }
      if (items.length) {
        changes.push({address: module.address, typeLabel, kind: 'changed', items});
      }
    });
    (this.moduleConfig?.uvsModule || [])
      .filter(gw => !isReadOnlyModuleType(gw.typ))
      .filter(gw => !modules.some(m => +m.address === this.normalizeAddress(gw.typ, gw.address)))
      .forEach(gw => changes.push({
        address: this.normalizeAddress(gw.typ, gw.address),
        typeLabel: this.getTypeLabel(gw.typ),
        kind: 'removed',
        items: []
      }));
    return changes;
  }

  private fmtValue(value: any, unit = ''): string {
    if (value === null || value === undefined || value === '') {
      return '–';
    }
    return String(value).replace('.', ',') + (unit ? ' ' + unit : '');
  }

  private configFields(source: {
    currentLoopThreshold?: number[], lightThreshold?: number, lekageThreshold?: number,
    temperatureThreshold?: number, irUartCfg?: { input: number, device: string }[]
  }): { key: string, label: string, value: string }[] {
    const out: { key: string, label: string, value: string }[] = [];
    /*source.currentLoopThreshold?.forEach((v, i) =>
      out.push({key: 'pump' + i, label: 'Grenzwert Pumpe ' + (i + 1), value: this.fmtValue(v, 'A')}));
    if (source.lightThreshold != null) {
      out.push({key: 'light', label: 'Schwelle Licht', value: this.fmtValue(source.lightThreshold)});
    }
    if (source.lekageThreshold != null) {
      out.push({key: 'leak', label: 'Schwelle Leckage', value: this.fmtValue(source.lekageThreshold)});
    }
    if (source.temperatureThreshold != null) {
      out.push({key: 'temp', label: 'Temperatur-Delta', value: this.fmtValue(source.temperatureThreshold, '°C')});
    }*/
    source.irUartCfg?.filter(ir => ir.input !== 254).forEach(ir =>
      out.push({key: 'ir' + ir.input, label: 'IR-Zähler Typ (Eingang ' + ir.input + ')', value: ir.device}));
    return out;
  }

  private describeConfig(module: UvsModuleModel): SendConfigChangeItem[] {
    return this.configFields(module).map(f => ({label: f.label, to: f.value}));
  }

  private diffConfig(module: UvsModuleModel, gw: MqttGetModuleConfigUvsModuleDto): SendConfigChangeItem[] {
    const old = this.configFields(gw);
    return this.configFields(module).reduce((items, f) => {
      const prev = old.find(o => o.key === f.key);
      if (!prev) {
        items.push({label: f.label, from: '–', to: f.value});
      } else if (prev.value !== f.value) {
        items.push({label: f.label, from: prev.value, to: f.value});
      }
      return items;
    }, [] as SendConfigChangeItem[]);
  }

  async sendModuleSelections() {
    this.configurationBeingSent = true;

    const modulesRequest = this.model.uvsModules
      .filter(module => module?.uvsType && !module.markedForRemoval && !isReadOnlyModuleType(module.uvsType))
      .flatMap(module => {
        return {
          ...module,
          type: module?.uvsType,
        };
      });

    const response = await this.dataService.postSetConfig(this.gatewayId, modulesRequest);

    for (let i = 0; i < this.configWaitTime; i++) {
      this.timerForConfigWaitTimeSubject.next(i);
      await this.delay(1000);
    }

    this.timerForConfigWaitTimeSubject.next(null);

    await this.deviceDataService.reloadDeviceData();
    await this.deviceDataService.loadModuleConfig(this.gatewayId);

    this.configurationBeingSent = false;
  }

  public async loadModuleConfig() {
    await this.deviceDataService.loadModuleConfig(this.shareService.getGatewayId());
  }

  async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public async loadCustomerAndSitesData() {
    if (this.model.selectedCustomer) {
      const customerId = this.model.selectedCustomer.customerId;
      const customers = this.customerOptions.filter(customer => customer.customerId === customerId);
      if (customers?.length === 1) {
        this.model.selectedCustomer = customers[0];
      }
      this.selectedCustomerChange(customers?.length === 1 ? customers[0] : null, false);

      if (customers.length === 1 && this.model?.selectedSite) {
        const siteId = this.model.selectedSite.id;
        let sites;
        if (this.model.additionalGateway) {
          sites = customers[0].installedSites.filter(site => site.id === siteId);
        } else {
          sites = customers[0].plannedSites.filter(site => site.id === siteId);
        }
        this.selectedSiteChanged(sites?.length === 1 ? sites[0] : null);
      }
    } else {
      this.selectedCustomerChange(null);
    }
  }

  public getSiteName(site: AvailableSite) {
    return `${site.street} ${site.streetNumber}, ${site.city} ${site.postCode}` + (site.additionalName ? ' - ' + site.additionalName : '');
  }

  public selectedCustomerChange(customer: Customer, clearAddress = true) {
    console.log(customer)
    if (!customer || customer.customerId === undefined) {
      this.model.selectedCustomer = undefined;
    } else {
      this.model.selectedCustomer = customer;
    }

    if (!this.model.additionalGateway && customer?.plannedSites?.length > 0) {
      customer?.plannedSites.sort((a, b) => this.getSiteName(a).localeCompare(this.getSiteName(b)));
      this.plannedSiteOptions = customer.plannedSites;
    }
    if (this.model.additionalGateway && customer?.installedSites?.length > 0) {
      customer?.installedSites.sort((a, b) => this.getSiteName(a).localeCompare(this.getSiteName(b)));
      this.installedSiteOptions = customer.installedSites;
    }
  }

  public selectedSiteChanged(site: AvailableSite) {
    if (site) {
      this.updateAddress(site);
    }
  }

  public updateAddress(site: AvailableSite) {
    this.model.selectedSite = site;
  }

  public showModuleConfiguration() {
    this.dialog.open(ModuleConfigurationModalComponent)
  }

  compareCustomers(c1: Customer, c2: Customer): boolean {
    return c1 && c2 ? c1.customerName === c2.customerName : c1 === c2;
  }

  compareById(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  private compareNumberOfCurrentModulesToNumberOfModulesFromDeviceData(): number {
    const deviceData = this.deviceDataService.getDeviceData();
    if (!deviceData?.deviceData?.uvsModule) {
      return 0;
    }
    return this.activeModulesCount - deviceData.deviceData.uvsModule.length;
  }

  protected readonly share = share;
}
